import type { ReactNode } from "react";
import { StyleSheet, Text, View, type TextStyle } from "react-native";

import type { UiTheme } from "../theme";

// The big tabular-nums value treatment, exported so a composite block that must
// render the number inline with other chrome (e.g. CaloriesEffortBlock's number
// + (i) pill) reuses it instead of re-declaring the four props — one source for
// the metric value look.
export const metricValueTextStyle = (theme: UiTheme): TextStyle => ({
  color: theme.palette.ink,
  fontSize: theme.typography.sizes.lg,
  fontWeight: "700",
  fontVariant: ["tabular-nums"],
});

// THE session-summary metric block: an uppercase quiet title over a big
// tabular-nums value (or arbitrary content). The title-first vertical stack the
// summary surfaces hand-rolled per file — Volume/Duration/Density in
// SessionSummaryView + the parallel copy in mobile's PostSessionSummaryCard —
// re-rolling the same metricBlock/metricTitle/metricValue StyleSheet. One place
// so every metric (including the new Calories/Effort block) reads identically
// ([[feedback_analytics_display]] "same title-first pattern for every metric").
//
// Pass `value` for the simple big-number case; pass `children` for composite
// content (e.g. a number + a source pill + a secondary line).
export type MetricBlockProps = {
  theme: UiTheme;
  title: string;
  /** Simple big-number value. Ignored when `children` is provided. */
  value?: string;
  children?: ReactNode;
};

export function MetricBlock({ theme, title, value, children }: MetricBlockProps) {
  const styles = makeStyles(theme);
  return (
    <View style={styles.block}>
      <Text style={styles.title} accessibilityRole="header">
        {title}
      </Text>
      {children ?? <Text style={styles.value}>{value}</Text>}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    block: {
      gap: s.s2,
      paddingTop: s.s2,
      borderTopColor: p.border,
      borderTopWidth: 1,
    },
    title: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: "700",
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    value: metricValueTextStyle(theme),
  });
}
